import { ForhandlerStoryblok } from "@/types/blok-types";
import { useDeferredValue, useMemo } from "react";
import { type StoryblokStory } from "storyblok-generate-ts";
import { create } from "zustand";

interface DealerFilters {
  search: string;
  country: string;
  city: string;
  setSearch: (search: string) => void;
  setCountry: (country: string) => void;
  setCity: (city: string) => void;
}

/**
 * Zustand store for dealer filters
 */
export const useDealerFilters = create<DealerFilters>((set) => ({
  search: "",
  country: "",
  city: "",
  setSearch: (search) => set({ search }),
  setCountry: (country) => set({ country }),
  setCity: (city) => set({ city })
}));

/**
 * Filters dealers based on search, country and city.
 * Receives filters from `useDealerFilters` store.
 */
export function useFilterDealers(data: StoryblokStory<ForhandlerStoryblok>[]) {
  const { search, country, city } = useDealerFilters();

  const deferredSearch = useDeferredValue(search);
  const deferredCity = useDeferredValue(city);

  const filteredDealers = useMemo(() => {
    if (!deferredSearch && !country && !deferredCity) return data;

    const query = deferredSearch.toLowerCase();
    return data?.filter(
      (dealer) =>
        dealer.content?.name?.toLowerCase().includes(query) &&
        dealer.content.country?.toLowerCase().includes(country.toLowerCase()) &&
        dealer.content.city?.toLowerCase().includes(deferredCity.toLowerCase())
    );
  }, [data, deferredSearch, country, deferredCity]);

  return filteredDealers;
}
